<template>
  <ModalAction>
    <template #content>
      <div class="mt-8 text-center">
        <p class="font-bold text-dark text-xl mb-2">{{ errorTitle }}</p>
        <p class="mb-4">{{ errorDescription }}</p>
        <section v-if="store.error?.code === '01-0022'">
          <div v-for="variantID in store.error['extra-args']" :key="variantID" class="mb-1">
            <p class="text-dark font-semibold">{{ getProductTitle(variantID) }}</p>
          </div>
          <div class="mt-4">
            <ButtonMain :text="t('updateOrder')" :type="1" class="mx-auto" :loading="loading" @click="removeOutOfStockProducts(store.error!['extra-args']), (loading = true)" />
          </div>
        </section>
        <section v-else class="flex items-center justify-center space-x-3">
          <ButtonMain v-if="showContactForm" :text="t('contactSupport')" :type="ButtonType.WhiteGreen" @click="emit('support', store.error?.order)" />
          <ButtonMain :text="t('close')" :type="ButtonType.Primary" class="mx-auto" @click="closeError" />
        </section>
      </div>
    </template>
  </ModalAction>
</template>
<script setup lang="ts">
  import * as Sentry from '@sentry/vue'
  import { useMiioErrors } from '~/composables/errors'
  import { useStore } from '~/stores/main'
  import { getBasketContent, getProductTitle, removeOutOfStockProducts } from '~/composables/useFunctions'
  import { ButtonType, CheckoutTab } from '~/types/enums'

  const { t, locale } = useI18n()
  const store = useStore()

  const emit = defineEmits(['close', 'support'])
  const loading = ref(false)
  const route = useRoute()
  const checkoutTab = useState('checkoutTab')

  const errorTitle = ref(t('somethingWentWrong'))
  const errorDescription = ref(t('tryAgainLater'))

  function replaceErrorArguments(miioError: MiioError) {
    if (!miioError['show-generic-error']) {
      const title = miioError.title?.[locale.value]
      const description = miioError.description?.[locale.value]
      const extraArgs = miioError?.['extra-args']
      if (extraArgs) {
        let newTitle = title
        let newDescription = description
        extraArgs.forEach((argName) => {
          const argValue = store.error['extra-args'][argName]
          const argPlaceholder = '$' + argName
          if (newTitle?.includes(argPlaceholder)) {
            newTitle = title?.replace(argPlaceholder, argValue)
          }
          if (newDescription?.includes(argPlaceholder)) {
            newDescription = description?.replace(argPlaceholder, argValue)
          }
        })
        errorTitle.value = newTitle || t('somethingWentWrong')
        errorDescription.value = newDescription || t('tryAgainLater')
      } else {
        errorTitle.value = title || t('somethingWentWrong')
        errorDescription.value = description || t('tryAgainLater')
      }
    }
  }

  if (store.error?.title && store.error?.description) {
    errorTitle.value = store.error.title
    errorDescription.value = store.error.description
  } else if (store.error?.code) {
    const miioErrorsList: MiioErrors = useMiioErrors()
    const miioError = miioErrorsList[store.error.code] as MiioError
    if (miioError) {
      replaceErrorArguments(miioError)
      if (miioError?.['show-generic-error']) {
        const sentryError = JSON.stringify(miioError)
        Sentry.captureException(new Error(sentryError))
      }
    }
  }

  function closeError() {
    if (store.error?.code === '01-0032') {
      if (route.fullPath.includes('checkout')) {
        changeRoute('account-orders')
      } else {
        reloadNuxtApp()
      }
    } else if (store.error?.code === '01-0040' || store.error?.code === '01-0041') {
      getBasketContent()
      checkoutTab.value = CheckoutTab.Shipping
    }
    emit('close')
  }

  const showContactForm = computed(() => {
    const errorCodes = ['01-0026', '01-0027', '01-0028', '01-0029']
    return errorCodes.includes(store.error?.code)
  })
</script>
